import { useState, useEffect, Component, ErrorInfo, ReactNode } from 'react';
import { auth, db, ADMIN_EMAIL } from './firebase';
import { LoginScreen } from './components/LoginScreen';
import { CommercialProposal } from './components/CommercialProposal';
import firebase from 'firebase/compat/app';
import { Toaster } from 'sonner';

// Error Boundary Component
interface ErrorBoundaryProps {
    children: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Application Error:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-100">
                    <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
                        <h1 className="text-2xl font-bold text-red-600 mb-4">Klaida</h1>
                        <p className="text-gray-700 mb-4">
                            Atsiprašome, bet įvyko klaida kraunant aplikaciją.
                        </p>
                        <details className="mb-4">
                            <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
                                Techninė informacija
                            </summary>
                            <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto">
                                {this.state.error?.message}
                                {'\n\n'}
                                {this.state.error?.stack}
                            </pre>
                        </details>
                        <button
                            onClick={() => window.location.reload()}
                            className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
                        >
                            Perkrauti puslapį
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

function App() {
    const [user, setUser] = useState<firebase.User | null>(null);
    const [initializing, setInitializing] = useState(true);
    const [loginError, setLoginError] = useState<string | null>(null);

    useEffect(() => {
        try {
            const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
                setLoginError(null);
                if (currentUser) {
                    const userEmail = currentUser.email?.toLowerCase();

                    if (!userEmail) {
                        setLoginError("Nepavyko gauti el. pašto adreso.");
                        await auth.signOut();
                        setUser(null);
                        setInitializing(false);
                        return;
                    }

                    // 1. Check if Super Admin
                    if (userEmail === ADMIN_EMAIL.toLowerCase()) {
                        setUser(currentUser);
                        setInitializing(false);
                        return;
                    }

                    // 2. Check allowedUsers collection
                    try {
                        const userDocRef = db.collection("allowedUsers").doc(userEmail);
                        const docSnap = await userDocRef.get();

                        if (docSnap.exists) {
                            setUser(currentUser);
                        } else {
                            console.warn(`Neautorizuotas bandymas prisijungti: ${userEmail}`);
                            setLoginError(`Jūsų paskyra (${userEmail}) nėra autorizuota. Susisiekite su administratoriumi.`);
                            await auth.signOut();
                            setUser(null);
                        }
                    } catch (error) {
                        console.error("Klaida tikrinant autorizaciją:", error);
                        setLoginError("Įvyko klaida tikrinant jūsų prieigą. Patikrinkite Firestore nustatymus.");
                        await auth.signOut();
                        setUser(null);
                    }

                } else {
                    setUser(null);
                }
                setInitializing(false);
            });

            return () => unsubscribe();
        } catch (error) {
            console.error("Firebase initialization error:", error);
            setLoginError("Nepavyko inicializuoti Firebase. Patikrinkite interneto ryšį.");
            setInitializing(false);
        }
    }, []);

    if (initializing) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <h1 className="text-xl">Kraunama...</h1>
            </div>
        );
    }

    if (!user) {
        return <LoginScreen loginError={loginError} />;
    }

    return <CommercialProposal user={user} />;
}

// Wrap App with ErrorBoundary and Toaster
function AppWithErrorBoundary() {
    return (
        <ErrorBoundary>
            <App />
            <Toaster
                position="top-right"
                richColors
                closeButton
                toastOptions={{
                    duration: 3000,
                }}
            />
        </ErrorBoundary>
    );
}

export default AppWithErrorBoundary;

